import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import { defineMessage } from '@lingui/macro'
import { useCallback } from 'react'
import ReactSelect, {
  components,
  type DropdownIndicatorProps,
  type MultiValue,
  type MultiValueProps,
  type MultiValueRemoveProps,
  type OptionProps,
  type ValueContainerProps,
} from 'react-select'
import styled, { useTheme } from 'styled-components'

export type MultiSelectOption = {
  value: string
  label: string
}

type MultiSelectProps = {
  name: string
  value: MultiValue<MultiSelectOption>
  placeholder?: string
  options: MultiValue<MultiSelectOption>
  onChange: (selected: MultiValue<MultiSelectOption>) => void
  onMenuOpen?: () => void
  /** When provided, replaces individual chips with a summary string (e.g. "3 artsen geselecteerd") */
  selectionSummary?: (count: number) => string
}

const selectFieldPlaceholder = defineMessage({
  id: 'select-field.placeholder',
  comment: 'Generic placeholder for the MultiSelect field.',
  message: 'Maak een keuze',
})

const MultiSelect = ({
  name,
  value,
  placeholder,
  options,
  onChange,
  onMenuOpen,
  selectionSummary,
}: MultiSelectProps) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()

  // Render Methods
  // ------------------------------------------------------------------------- /

  const OptionWithCheckbox = useCallback((props: OptionProps<MultiSelectOption, true>) => {
    const { isSelected, label } = props
    return (
      <components.Option {...props}>
        <StyledOptionRow>
          <StyledCheckbox role='checkbox' aria-checked={isSelected}>
            {isSelected ? <StyledCheckIcon icon={['fal', 'check']} /> : null}
          </StyledCheckbox>
          <span>{label}</span>
        </StyledOptionRow>
      </components.Option>
    )
  }, [])

  const MultiValueRemove = useCallback(
    (props: MultiValueRemoveProps<MultiSelectOption, true>) => (
      <components.MultiValueRemove {...props}>
        <FontAwesomeIcon icon={['fal', 'xmark']} />
      </components.MultiValueRemove>
    ),
    [],
  )

  const SearchDropdownIndicator = useCallback(
    (props: DropdownIndicatorProps<MultiSelectOption, true>) => (
      <components.DropdownIndicator {...props}>
        <FontAwesomeIcon icon={['fal', 'magnifying-glass']} color={theme.Components.Input.Text} />
      </components.DropdownIndicator>
    ),
    [theme],
  )

  const SummaryValueContainer = useCallback(
    (props: ValueContainerProps<MultiSelectOption, true>) => {
      const selectedCount = props.getValue().length

      if (selectedCount < 3) {
        return <components.ValueContainer {...props} />
      }

      return (
        <components.ValueContainer {...props}>
          <StyledSummaryText>{selectionSummary!(selectedCount)}</StyledSummaryText>
          {/* Render the search input */}
          {Array.isArray(props.children) && props.children.at(-1)}
        </components.ValueContainer>
      )
    },
    [selectionSummary],
  )

  // Hide individual chips when 3+ are selected in summary mode
  const SummaryMultiValue = useCallback((props: MultiValueProps<MultiSelectOption, true>) => {
    const selectedCount = props.selectProps.value
      ? (props.selectProps.value as MultiValue<MultiSelectOption>).length
      : 0

    if (selectedCount >= 3) return null

    return <components.MultiValue {...props} />
  }, [])

  return (
    <div>
      <ReactSelect
        name={name}
        isMulti
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        hideSelectedOptions={false}
        options={options}
        value={value}
        placeholder={placeholder ?? i18n._(selectFieldPlaceholder)}
        isClearable={false}
        onChange={(newSelectedOptions) => {
          onChange(newSelectedOptions)
        }}
        onMenuOpen={onMenuOpen}
        components={{
          Option: OptionWithCheckbox,
          DropdownIndicator: SearchDropdownIndicator,
          MultiValueRemove,
          ...(selectionSummary
            ? {
                ValueContainer: SummaryValueContainer,
                MultiValue: SummaryMultiValue,
              }
            : {}),
        }}
        styles={{
          menuList: (base) => ({
            ...base,
            maxHeight: '316px !important',
          }),
          control: (base) => ({
            ...base,
            padding: `${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4}`,
            backgroundColor: theme.Components.Input.Fill,
            border: `1px solid ${theme.Components.Input.Border}`,
            borderRadius: `${theme.UI.BorderRadius.Small}px`,
            color: theme.Components.Input.Text,
            minHeight: 'auto',
            '&:focus-within': {
              outline: 'none',
              borderColor: theme.Components.Input.ActiveBorder,
              backgroundColor: theme.Components.Input.ActiveFill,
            },
          }),
          option: (base, state) => ({
            ...base,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            paddingTop: theme.UI.SpacingPx.Space2,
            paddingBottom: theme.UI.SpacingPx.Space2,
            backgroundColor: state.isSelected
              ? theme.Components.Card.ActiveFill
              : state.isFocused
                ? theme.Colors.Neutrals[50]
                : theme.Components.Card.Fill,
            color: theme.Components.Card.Text,
            cursor: 'pointer',
          }),
          valueContainer: (base) => ({
            ...base,
            flexWrap: 'nowrap' as const,
            overflow: 'hidden',
          }),
          input: (base) => ({
            ...base,
            color: theme.Components.Input.Text,
          }),
          placeholder: (base) => ({
            ...base,
            color: theme.Components.Input.Text,
            opacity: 0.6,
            margin: 0,
            padding: 0,
          }),
          multiValue: (base) => ({
            ...base,
            backgroundColor: '#eff1f6',
            borderRadius: '2px',
            padding: `2px ${theme.UI.SpacingPx.Space2}`,
            gap: theme.UI.SpacingPx.Space2,
            flexShrink: 0,
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: theme.Components.Input.Text,
            fontSize: theme.Typography.BodyMediumRegular['font-size'],
            padding: 0,
            paddingLeft: 0,
          }),
          multiValueRemove: (base) => ({
            ...base,
            color: theme.Components.Input.Text,
            cursor: 'pointer',
            padding: 0,
            ':hover': {
              backgroundColor: 'transparent',
              color: theme.Components.Input.Text,
            },
          }),
          indicatorSeparator: () => ({
            display: 'none',
          }),
          dropdownIndicator: (base) => ({
            ...base,
            padding: 0,
            color: theme.Components.Input.Text,
          }),
        }}
      />
    </div>
  )
}

// Styled Components
// ------------------------------------------------------------------------- /
const StyledSummaryText = styled.span`
  color: ${({ theme }) => theme.Components.Input.Text};
  font-size: ${({ theme }) => theme.Typography.BodyMediumRegular['font-size']};
`

const StyledOptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};
`

const StyledCheckbox = styled.div`
  position: relative;
  overflow: hidden;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  max-width: 16px;
  max-height: 16px;
  margin-left: 1px;
  margin-top: 3px;
  flex-grow: 0;
  border: 1px solid ${({ theme }) => theme.Colors.Neutrals.Darkest};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  background: transparent;
  pointer-events: auto;
`

const StyledCheckIcon = styled(FontAwesomeIcon)`
  position: absolute;
  width: 12px;
  height: 12px;
  top: 2px;
  left: 2px;
  bottom: 2px;
  right: 2px;
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
`

export { MultiSelect }
